import React, { useState } from 'react';
import {
  PrimaryButton,
  ButtonSizes,
} from 'app/common/components/controls/button/button';
import styles from './MoonshotCreateTeamForm.less';
import Select, { components } from 'react-select';
import _ from 'underscore';
import {
  useMoonshotCreateTeamSubmitMutation,
  useMoonshotCopyBoardMutation,
  useMoonshotCreateTeamQuery,
} from '@trello/graphql/generated';
import { useAllUsersAreAdminsEnabled } from '@trello/organizations';
import { trackUe, Noun, Verb } from '@trello/analytics';
import { Spinner } from 'app/src/components/Spinner';
import Alerts from 'app/scripts/views/lib/alerts.coffee';
import { MoonshotSubmitFunction } from './types';
import { ErrorBoundary } from 'app/src/components/ErrorBoundary';
import { logError } from 'app/scripts/debug/log-error';
import { Team } from 'app/scripts/debug/constants';

const TEAM_ERROR_MSG = 'Please enter a team name.';
const CATEGORY_ERROR_MSG = 'Please select what you will be using Trello for.';

const boardsToCopy = [
  // Team Goal Tracker
  '5d6040a01b9d63548b2edaf9',
  // Manage any project
  '5d6040b0c2aa3e110a798545',
  // Run better meetings
  '5d6040bd99aa5878d2e7174b',
  // Keep Team Info Organized
  '5d6040ca785f435af362b6c6',
];

interface CreateTeamProps {
  onSubmit: MoonshotSubmitFunction;
}

interface CreateTeamFormComponentProps {
  readonly isFocused: boolean;
  readonly isSelected: boolean;
}

interface Option {
  value: string;
  label: string;
}

const items = _.shuffle<Option>([
  { value: 'it', label: 'IT' },
  { value: 'operations', label: 'Operations' },
  { value: 'sales', label: 'Sales' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'human resources', label: 'Human Resources' },
  { value: 'engineering', label: 'Engineering' },
  { value: 'product', label: 'Product' },
  { value: 'design', label: 'Design' },
  { value: 'support', label: 'Support' },
]).concat({ value: 'other', label: 'Other' });

const Input: React.FC<CreateTeamFormComponentProps> = ({ ...props }) => (
  <components.Input {...props} className={styles.createTeamMoonshotDropdown} />
);

export const MoonshotCreateTeamForm: React.FC<CreateTeamProps> = ({
  onSubmit,
}) => {
  const [teamName, setTeamName] = useState('');
  const [teamNameError, setTeamNameError] = useState('');
  const [category, setCategory] = useState('');
  const [categoryError, setCategoryError] = useState('');

  const allAdminsEnabled = useAllUsersAreAdminsEnabled();

  const onChangeTeamName: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setTeamName(e.target.value);
    setTeamNameError('');
  };

  const [
    createOrganization,
    { loading: creating },
  ] = useMoonshotCreateTeamSubmitMutation();

  const [copyBoardToOrg] = useMoonshotCopyBoardMutation();

  const onChangeCategory = ({ value }: Option) => {
    setCategory(value);
    setCategoryError('');
  };

  const onCreateTeamSubmit: React.FormEventHandler = async (e) => {
    e.preventDefault();

    if (creating) {
      return;
    }
    const trimmedName = teamName.trim();

    const teamNameHasError = trimmedName.length === 0;
    const categoryHasError = category.length === 0;

    if (teamNameHasError) {
      setTeamNameError(TEAM_ERROR_MSG);
    }

    if (categoryHasError) {
      setCategoryError(CATEGORY_ERROR_MSG);
    }

    if (teamNameHasError || categoryHasError) {
      return;
    }

    let result;
    try {
      const { data } = await createOrganization({
        variables: {
          type: 'default',
          displayName: trimmedName,
          allAdminsEnabled,
        },
      });
      result = data;
    } catch (err) {
      Alerts.showLiteralText(err.message, 'error', 'alert', 5000);
      logError({
        error: err,
        tags: { ownershipArea: Team.BizTeam },
      });
    }

    trackUe({
      category: Noun.MOONSHOT,
      verb: Verb.CREATES,
      directObj: Noun.TEAM,
      context: { vertical: category },
    });

    if (result && result.createOrganization) {
      const organizationId = result.createOrganization.id;
      try {
        await Promise.all(
          boardsToCopy.map((boardId) =>
            copyBoardToOrg({
              variables: {
                boardId,
                organizationId,
              },
            }),
          ),
        );
      } catch (err) {
        logError({
          error: err,
          tags: { ownershipArea: Team.BizTeam },
        });
      }

      onSubmit();
    }
  };

  const { data } = useMoonshotCreateTeamQuery({
    variables: {
      memberId: 'me',
    },
  });

  const username = (data && data.member && data.member.fullName) || '';

  return (
    <ErrorBoundary tags={{ ownershipArea: Team.BizTeam }}>
      <div className={styles.moonshotCreateTeamFormContent}>
        <div className={styles.moonshotCreateTeamFormHeadline}>
          <h1>
            <span role="img" aria-label="waving hand">
              👋
            </span>{' '}
            Welcome, <strong>{username}!</strong>
          </h1>
        </div>
        <form
          className={styles.moonshotCreateTeamForm}
          onSubmit={onCreateTeamSubmit}
        >
          <label
            className={styles.moonshotCreateTeamLabel}
            htmlFor="moonshotCreateTeam"
          >
            What is Your Team Named?
            <span className={styles.createTeamLabelstar}>*</span>
          </label>
          <input
            id="moonshotCreateTeam"
            className={styles.moonshotCreateTeamInput}
            type="text"
            autoComplete="off"
            spellCheck={false}
            onChange={onChangeTeamName}
            autoFocus={true}
            placeholder="i.e., Pawnee Parks Department"
            value={teamName}
            maxLength={100}
            minLength={1}
            aria-describedby="moonshotCreateTeamNameError"
          />
          <div
            id="moonshotCreateTeamNameError"
            role="alert"
            aria-atomic
            className={styles.error}
          >
            {!!teamNameError && <p>{teamNameError}</p>}
          </div>
          <label
            className={styles.moonshotCreateTeamLabel}
            htmlFor="moonshotCreateTeamCategory"
          >
            What will you be using Trello for?
            <span className={styles.createTeamLabelstar}>*</span>
          </label>
          <Select
            blurInputOnSelect={true}
            className={styles.moonshotCreateTeamDropdown}
            placeholder="Choose something..."
            options={items}
            inputId="moonshotCreateTeamCategory"
            aria-describedby="moonshotCreateTeamCategoryError"
            onChange={onChangeCategory}
            components={{ Input, IndicatorSeparator: () => null }}
          />
          <div
            id="moonshotCreateTeamCategoryError"
            role="alert"
            aria-atomic
            className={styles.error}
          >
            {!!categoryError && <p>{categoryError}</p>}
          </div>
          {/* TODO: Add Create Team Logo component: https://trello.com/c/F2z2Co1r/233-create-logo-upload-component */}
          <div className={styles.moonshotCreateTeamSubmitButtonHolder}>
            <PrimaryButton
              className={styles.moonshotCreateTeamSubmitButton}
              type="submit"
              size={ButtonSizes.FULLWIDTH}
              disabled={!(teamName && category) || creating}
              aria-label={creating ? 'Creating team' : undefined}
            >
              {creating ? (
                <Spinner centered />
              ) : (
                'Next, Invite Your Team Members'
              )}
            </PrimaryButton>
          </div>
        </form>
      </div>
    </ErrorBoundary>
  );
};
